import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Building2, Check, HandCoins, LogIn, Network, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Mark } from "../brand/Logo";
import { MaskLines } from "../motion/Reveal";
import { FlowRail } from "./FlowRail";
import { scrollToId } from "../../lib/scroll";
import { useSite } from "../../lib/site";

const FactoryIcon = (props) => <Mark className="h-9 w-9" {...props} />;
const EASE = [0.2, 0.8, 0.2, 1];

const Strike = ({ children }) => (
  <span className="relative inline-block text-white/45">
    {children}
    <motion.span
      aria-hidden="true"
      className="absolute left-[-2%] top-[54%] h-[0.09em] w-[104%] origin-left rounded-full bg-coral"
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 0.9, delay: 1.5, ease: [0.7, 0, 0.2, 1] }}
    />
  </span>
);

const HeroVisual = ({ hero, image }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
      className="relative mx-auto w-full max-w-[520px] [perspective:1400px]"
      data-testid="hero-visual"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="spotlight relative aspect-[4/5] overflow-hidden rounded-[2.5rem_2.5rem_2.5rem_8rem] border border-white/10 sm:aspect-[5/6]">
          <img src={image} alt="Ejecutivos de negocios sonriendo en una oficina moderna" className="h-full w-full scale-105 object-cover" />
          <div className="absolute inset-0 z-[2] bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
        </div>
        <div
          style={{ transform: "translateZ(90px)" }}
          className="absolute -top-4 right-3 flex items-center gap-2 rounded-full border border-white/15 bg-ink/80 px-4 py-2.5 backdrop-blur-xl sm:-right-6"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inset-0 animate-pulse-ring rounded-full bg-leaf" />
            <span className="relative h-2.5 w-2.5 rounded-full bg-leaf" />
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.1em] text-white">Cliente real</span>
        </div>
        <div
          style={{ transform: "translateZ(60px)" }}
          className="absolute -bottom-6 left-3 right-3 rounded-[1.6rem] border border-white/15 bg-ink/85 p-5 backdrop-blur-xl sm:-left-10 sm:right-auto sm:w-[330px]"
          data-testid="hero-flow-card"
        >
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-leaf">Así funciona</p>
          <ul className="mt-3 space-y-2.5">
            {hero.flow_lines.map((line, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.6 + i * 0.25, duration: 0.6, ease: EASE }}
                className="flex items-start gap-3 text-sm leading-snug text-white"
              >
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-leaf text-ink">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {line}
              </motion.li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = ({ onPlayIntro }) => {
  const { content } = useSite();
  const hero = content.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 220]);

  return (
    <section id="inicio" ref={ref} className="grain relative overflow-hidden bg-ink pb-16 pt-28 text-white sm:pt-32 lg:pb-24" data-testid="hero-section">
      <div className="grid-lines pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_30%_30%,black,transparent_75%)]" />
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-leaf/25 blur-[140px]" />
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute -right-32 top-60 h-[420px] w-[420px] rounded-full bg-azure/25 blur-[140px]" />

      <div className="container-x relative">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
          <motion.div style={{ y: yText }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] text-white/85"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-leaf" /> {hero.eyebrow}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.25 }}
              className="mt-7 max-w-xl font-mono text-sm uppercase leading-relaxed tracking-[0.14em] text-leaf"
              data-testid="hero-title"
            >
              {hero.title}
            </motion.h2>
            <MaskLines
              as="h1"
              onLoad
              delay={0.03}
              testId="hero-headline"
              className="mt-4 font-display text-[2.7rem] font-semibold leading-[1.02] tracking-[-0.045em] sm:text-7xl sm:leading-[0.92] lg:text-[5.4rem] xl:text-[6.2rem]"
              lines={[
                hero.line_1,
                <Strike key="s">{hero.line_2}</Strike>,
                hero.line_3,
                <span key="a" className="text-leaf">{hero.line_4}</span>,
              ]}
            />
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
              className="mt-8 max-w-xl space-y-3 text-base leading-relaxed text-[#C9D0E4] sm:text-lg"
            >
              <p data-testid="hero-text">{hero.text}</p>
              <p className="text-[#AEB7D3]">{hero.text_2}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
              className="mt-10 flex flex-col gap-3 sm:flex-row"
            >
              <button onClick={() => scrollToId("oportunidad")} data-testid="hero-cta-oportunidad" className="btn-leaf group">
                {hero.cta_primary} <ArrowDownRight className="h-5 w-5" />
              </button>
              <Link to="/registro" data-testid="hero-cta-distribuidor" className="btn-ghost-dark group">
                {hero.cta_secondary} <ArrowUpRight className="btn-arrow h-5 w-5" />
              </Link>
              <Link to="/login" data-testid="hero-cta-ingresar" className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-mono text-xs uppercase tracking-[0.12em] text-white/80 transition-colors hover:text-leaf">
                <LogIn className="h-4 w-4" /> Ingresar a mi oficina
              </Link>
              <button type="button" onClick={onPlayIntro} data-testid="hero-cta-intro" className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-mono text-xs uppercase tracking-[0.12em] text-white/80 transition-colors hover:text-leaf">
                <PlayCircle className="h-4 w-4" /> Ver intro
              </button>
            </motion.div>
          </motion.div>
          <div className="pb-6 lg:col-span-5">
            <HeroVisual hero={hero} image={content.media.hero_image} />
          </div>
        </div>

        <div className="mt-24 border-t border-white/10 pt-12 lg:mt-28">
          <p className="mb-10 font-mono text-xs uppercase tracking-[0.18em] text-[#AEB7D3]">El recorrido del valor</p>
          <FlowRail
            items={hero.flow}
            notes={hero.flow_notes}
            icons={[Building2, FactoryIcon, Network, HandCoins]}
            tone="dark"
            testId="hero-flow"
          />
        </div>
      </div>
    </section>
  );
};
